import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { NostrEvent, NostrMetadata } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useSeoMeta } from '@unhead/react';
import localforage from 'localforage';
import { nip19 } from 'nostr-tools';
import { SocialGraph } from 'nostr-social-graph';
import { ChevronUp, Radio, Sparkles, Wifi, WifiOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppContext } from '@/hooks/useAppContext';
import { getAvatarShape, getEmojiMaskUrl, type AvatarShape } from '@/lib/avatarShape';
import { sanitizeUrl } from '@/lib/utils';

const RELAY_OPTIONS = [
  'wss://relay.ditto.pub',
  'wss://relay.primal.net',
  'wss://relay.damus.io',
  'wss://nos.lol',
  'wss://nostr.wine',
  'wss://relay.snort.social',
  'wss://relayable.org',
];

const DEFAULT_RELAYS = [
  'wss://relay.ditto.pub',
  'wss://relay.primal.net',
  'wss://relay.damus.io',
];

const WOT_ROOT_OPTIONS = [
  { label: 'fiatjaf', value: 'npub180cvv07tjdrrgpa0j7j7tmnyl2yr6yr7l8j4s3evf6u64th6gkwsyjh6w6' },
  { label: 'derek', value: 'npub18ams6ewn5aj2n3wt2qawzglx9mr4nzksxhvrdc4gzrecw7n5tvjqctp424' },
  { label: 'odell', value: 'npub1qny3tkh0acurzla8x3zy4nhrjz5zd8l9sy9jys09umwng00manysew95gx' },
  { label: 'gigi', value: 'npub1dergggklka99wwrs92yz8wdjs952h2ux2ha2ed598ngwu9w7a6fsh9xzpc' },
];
const SOCIAL_GRAPH_API_URL = 'https://graph-api.iris.to/social-graph';
const WOT_GRAPH_CACHE_PREFIX = 'relay-orbit:wot-social-graph:v1';

const DEFAULT_PROFILE_IMAGE =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 160 160%22%3E%3Cdefs%3E%3CradialGradient id=%22g%22 cx=%2235%25%22 cy=%2225%25%22 r=%2275%25%22%3E%3Cstop offset=%220%25%22 stop-color=%22%23f0f9ff%22/%3E%3Cstop offset=%2245%25%22 stop-color=%22%238b5cf6%22/%3E%3Cstop offset=%22100%25%22 stop-color=%22%2306b6d4%22/%3E%3C/radialGradient%3E%3C/defs%3E%3Crect width=%22160%22 height=%22160%22 rx=%2280%22 fill=%22url(%23g)%22/%3E%3Ccircle cx=%2280%22 cy=%2265%22 r=%2228%22 fill=%22white%22 fill-opacity=%22.9%22/%3E%3Cpath d=%22M36 134c8-28 26-43 44-43s36 15 44 43%22 fill=%22white%22 fill-opacity=%22.78%22/%3E%3C/svg%3E';

const MAX_BALLS = 112;
const MAX_ROOT_BALLS = 18;
const BUBBLE_SPAWN_RATE_WINDOW = 30_000;
const AUTO_BUBBLE_TARGET_MIN = 18;
const AUTO_BUBBLE_TARGET_MAX = 72;
const MANUAL_BUBBLE_TARGET_MIN = 8;
const MANUAL_BUBBLE_TARGET_MAX = 96;
const PERSISTENT_AREA_BUDGET = 0.18;
const NOTE_RADIUS_RANGE = [28, 74] as const;
const ROOT_RADIUS_RANGE = [34, 58] as const;
const ZAP_RADIUS_RANGE = [15, 28] as const;
const REACTION_RADIUS_RANGE = [18, 30] as const;
const ZAP_TRAIL_DURATION = 1_850;
const EXPLOSION_DURATION = 560;
const MAX_CRACKS = 2;
const EXPLOSION_PARTICLE_ANGLES = Array.from({ length: 9 }, (_, i) => (i * Math.PI * 2) / 9);
const PROFILE_CACHE_LIMIT = 500;
const ROOT_CACHE_LIMIT = 400;
const PROFILE_MENTION_REGEX = /(?:nostr:|@)?(?:nprofile|npub)1[023456789acdefghjklmnpqrstuvwxyz]+/gi;
const HEX_EVENT_ID_REGEX = /^[0-9a-f]{64}$/i;

type ConnectionState = 'connecting' | 'live' | 'reconnecting' | 'offline';
type WotStatus = 'disabled' | 'resolving' | 'hydrating' | 'ready' | 'error';
type BallRole = 'note' | 'reply' | 'root' | 'zap' | 'reaction';

type TrailPoint = {
  x: number;
  y: number;
  time: number;
};

type Ball = {
  id: string;
  eventId: string;
  pubkey: string;
  imageUrl: string;
  shape?: AvatarShape;
  messagePreview: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  createdAt: number;
  expiresAt: number;
  hue: number;
  role: BallRole;
  rootEventId?: string;
  replyEventId?: string;
  zapAmountSats?: number;
  trail?: TrailPoint[];
  enteredViewport?: boolean;
  cracks?: number;
  explodingAt?: number;
  targetSpeed?: number;
};

type ProfileResult = {
  imageUrl: string;
  shape?: AvatarShape;
};

type MentionProfile = {
  pubkey: string;
  mention: string;
};

type ThreadEventReference = {
  eventId: string;
  relayHint?: string;
  authorHint?: string;
};

type ThreadReference = {
  root: ThreadEventReference;
  reply?: ThreadEventReference;
};

type SpawnOptions = {
  role?: BallRole;
  rootEventId?: string;
  replyEventId?: string;
  reuseExisting?: boolean;
  zapAmountSats?: number;
  radius?: number;
  maxRadius?: number;
};

type ThreadLink = {
  id: string;
  from: Ball;
  to: Ball;
  relation: 'root' | 'reply';
};

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function clampNumber(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function getAutoBubbleTarget(width: number, height: number) {
  return Math.round(clampNumber((width * height) / 45_000, AUTO_BUBBLE_TARGET_MIN, AUTO_BUBBLE_TARGET_MAX));
}

function isPersistentBallRole(role: BallRole) {
  return role === 'note' || role === 'reply' || role === 'root';
}

function getDiscArea(radius: number) {
  return Math.PI * radius * radius;
}

function pruneForBubbleArea(balls: Ball[], incomingArea: number, stageArea: number) {
  const budget = stageArea * PERSISTENT_AREA_BUDGET;
  let currentArea = incomingArea;
  for (const ball of balls) {
    if (isPersistentBallRole(ball.role)) currentArea += getDiscArea(ball.radius);
  }
  if (currentArea <= budget) return balls;

  const evictable = balls
    .filter((ball) => isPersistentBallRole(ball.role))
    .sort((a, b) => a.createdAt - b.createdAt);
  const toEvict = new Set<string>();
  for (const ball of evictable) {
    if (currentArea <= budget) break;
    toEvict.add(ball.id);
    currentArea -= getDiscArea(ball.radius);
  }
  return toEvict.size > 0 ? balls.filter((ball) => !toEvict.has(ball.id)) : balls;
}

function getBubbleLifetime(role: BallRole, targetCount: number, spawnRatePerSecond: number) {
  const targetLifetime = targetCount / Math.max(spawnRatePerSecond, 1 / (BUBBLE_SPAWN_RATE_WINDOW / 1000));
  const roleMultiplier = role === 'root' ? 1.24 : role === 'zap' ? 0.95 : role === 'reaction' ? 0.26 : 1;
  const lifetime = targetLifetime * roleMultiplier * 1000;

  if (role === 'root') return clampNumber(lifetime, 14_000, 70_000);
  if (role === 'zap') return clampNumber(lifetime, 12_000, 50_000);
  if (role === 'reaction') return clampNumber(lifetime, 3_500, 12_000);
  return clampNumber(lifetime, 8_000, 60_000);
}

function getRoleRadiusRange(role: BallRole) {
  if (role === 'root') return ROOT_RADIUS_RANGE;
  if (role === 'zap') return ZAP_RADIUS_RANGE;
  if (role === 'reaction') return REACTION_RADIUS_RANGE;
  return NOTE_RADIUS_RANGE;
}

function normalizeRelayUrl(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';

  try {
    const prefixed = trimmed.includes('://') ? trimmed : `wss://${trimmed}`;
    const url = new URL(prefixed);
    if (url.protocol !== 'wss:' && url.protocol !== 'ws:') return '';
    url.hash = '';
    url.search = '';
    return url.toString().replace(/\/$/, '');
  } catch {
    return '';
  }
}

function isHexPubkey(value: string) {
  return HEX_EVENT_ID_REGEX.test(value);
}

async function resolveNip05Pubkey(identifier: string, signal: AbortSignal) {
  const trimmed = identifier.trim();
  const parts = trimmed.split('@');
  if (parts.length > 2) throw new Error('Enter a valid NIP-05 identifier.');

  const [rawName, rawDomain] = parts.length === 2 ? parts : ['_', trimmed];
  const name = rawName?.trim();
  const domain = rawDomain?.trim().toLowerCase();

  if (!name || !domain || !/^[a-z0-9._-]+$/i.test(name) || /[\s/@?#]/.test(domain)) {
    throw new Error('Enter a valid NIP-05 identifier.');
  }

  const url = new URL(`https://${domain}/.well-known/nostr.json`);
  url.searchParams.set('name', name);

  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error('NIP-05 lookup failed.');

  const data = await response.json() as unknown;
  if (!data || typeof data !== 'object') throw new Error('NIP-05 response was malformed.');

  const names = (data as { names?: unknown }).names;
  if (!names || typeof names !== 'object') throw new Error('NIP-05 response had no names.');

  const pubkey = (names as Record<string, unknown>)[name];
  if (typeof pubkey !== 'string' || !isHexPubkey(pubkey)) {
    throw new Error('NIP-05 identifier did not resolve to a pubkey.');
  }

  return pubkey.toLowerCase();
}

async function resolveWotRootPubkey(value: string, signal: AbortSignal) {
  const trimmed = value.trim();
  if (!trimmed) throw new Error('Enter a root npub, NIP-05, or hex pubkey.');

  if (isHexPubkey(trimmed)) return trimmed.toLowerCase();

  if (trimmed.toLowerCase().startsWith('npub1')) {
    try {
      const decoded = nip19.decode(trimmed);
      if (decoded.type === 'npub' && typeof decoded.data === 'string' && isHexPubkey(decoded.data)) {
        return decoded.data.toLowerCase();
      }
    } catch {
      throw new Error('Invalid npub identifier.');
    }

    throw new Error('Root must be an npub, NIP-05, or hex pubkey.');
  }

  return resolveNip05Pubkey(trimmed, signal);
}

function getWotGraphCacheKey(rootPubkey: string) {
  return `${WOT_GRAPH_CACHE_PREFIX}:${rootPubkey}`;
}

function toCachedGraphBinary(value: unknown) {
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  return undefined;
}

function parseMetadata(content: string): NostrMetadata | undefined {
  try {
    const parsed = JSON.parse(content) as unknown;
    if (!parsed || typeof parsed !== 'object') return undefined;
    return parsed as NostrMetadata;
  } catch {
    return undefined;
  }
}

function getProfileImage(event: NostrEvent | undefined) {
  const metadata = event ? parseMetadata(event.content) : undefined;
  const candidate = typeof metadata?.picture === 'string' ? metadata.picture : undefined;
  return sanitizeUrl(candidate) ?? DEFAULT_PROFILE_IMAGE;
}

function getProfileShape(event: NostrEvent | undefined): AvatarShape | undefined {
  const metadata = event ? parseMetadata(event.content) : undefined;
  return getAvatarShape(metadata as { [key: string]: unknown } | undefined);
}

function getDisplayName(event: NostrEvent | undefined) {
  const metadata = event ? parseMetadata(event.content) : undefined;
  const candidate = metadata?.display_name || metadata?.name;
  if (typeof candidate !== 'string') return undefined;

  const displayName = candidate.trim().replace(/\s+/g, ' ');
  return displayName || undefined;
}

function getTagValue(event: NostrEvent, tagName: string) {
  return event.tags.find(([name]) => name === tagName)?.[1];
}

function getZapSenderPubkey(event: NostrEvent) {
  const directSender = getTagValue(event, 'P');
  if (directSender && HEX_EVENT_ID_REGEX.test(directSender)) return directSender.toLowerCase();

  const description = getTagValue(event, 'description');
  if (!description) return undefined;

  try {
    const zapRequest = JSON.parse(description) as unknown;
    if (!zapRequest || typeof zapRequest !== 'object') return undefined;
    const pubkey = (zapRequest as { pubkey?: unknown }).pubkey;
    return typeof pubkey === 'string' && HEX_EVENT_ID_REGEX.test(pubkey) ? pubkey.toLowerCase() : undefined;
  } catch {
    return undefined;
  }
}

function parseBolt11AmountSats(invoice: string | undefined) {
  if (!invoice) return undefined;
  const match = invoice.toLowerCase().match(/^ln(?:bc|tb|bcrt)(\d+)([munp]?)/);
  if (!match) return undefined;

  const quantity = Number.parseInt(match[1], 10);
  if (!Number.isFinite(quantity) || quantity <= 0) return undefined;

  const multiplier = match[2];
  const sats = multiplier === 'm'
    ? quantity * 100_000
    : multiplier === 'u'
      ? quantity * 100
      : multiplier === 'n'
        ? quantity / 10
        : multiplier === 'p'
          ? quantity / 10_000
          : quantity * 100_000_000;

  return Math.max(1, Math.round(sats));
}

function getZapAmountSats(event: NostrEvent) {
  const candidates = [getTagValue(event, 'amount')];

  const description = getTagValue(event, 'description');
  if (description) {
    try {
      const zapRequest = JSON.parse(description) as unknown;
      if (zapRequest && typeof zapRequest === 'object') {
        const tags = (zapRequest as { tags?: unknown }).tags;
        if (Array.isArray(tags)) {
          const amountTag = tags.find((tag): tag is string[] => (
            Array.isArray(tag) && tag[0] === 'amount' && typeof tag[1] === 'string'
          ));
          candidates.push(amountTag?.[1]);
        }
      }
    } catch {
      // Ignore malformed zap requests; many receipts include a top-level amount tag.
    }
  }

  for (const candidate of candidates) {
    if (!candidate) continue;
    const millisats = Number.parseInt(candidate, 10);
    if (Number.isFinite(millisats) && millisats > 0) return Math.max(1, Math.round(millisats / 1000));
  }

  return parseBolt11AmountSats(getTagValue(event, 'bolt11'));
}

function formatSats(amount: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(amount);
}

function preloadImage(src: string, signal: AbortSignal) {
  if (src === DEFAULT_PROFILE_IMAGE || signal.aborted) return Promise.resolve(src);

  return new Promise<string>((resolve) => {
    const image = new Image();
    const finish = (url: string) => {
      image.onload = null;
      image.onerror = null;
      resolve(url);
    };

    image.onload = () => finish(src);
    image.onerror = () => finish(DEFAULT_PROFILE_IMAGE);
    image.decoding = 'async';
    image.referrerPolicy = 'no-referrer';
    image.src = src;

    if (signal.aborted) finish(DEFAULT_PROFILE_IMAGE);
  });
}

function decodeMentionPubkey(match: string) {
  try {
    const clean = match.replace(/^(?:nostr:|@)/i, '');
    const decoded = nip19.decode(clean);

    if (decoded.type === 'npub') return decoded.data;
    if (decoded.type === 'nprofile') return decoded.data.pubkey;
    return undefined;
  } catch {
    return undefined;
  }
}

function getMentionProfiles(content: string) {
  const profiles: MentionProfile[] = [];
  const seen = new Set<string>();

  for (const match of content.matchAll(PROFILE_MENTION_REGEX)) {
    const pubkey = decodeMentionPubkey(match[0]);
    if (!pubkey || seen.has(pubkey)) continue;
    seen.add(pubkey);
    profiles.push({ pubkey, mention: '@unknown' });
  }

  return profiles;
}

function getRelayHint(tag: string[]) {
  const relay = normalizeRelayUrl(tag[2] ?? '');
  return relay || undefined;
}

function getAuthorHint(tag: string[]) {
  const candidate = tag[4];
  return candidate && /^[0-9a-f]{64}$/i.test(candidate) ? candidate.toLowerCase() : undefined;
}

function getThreadEventReference(tag: string[]): ThreadEventReference {
  return {
    eventId: tag[1].toLowerCase(),
    relayHint: getRelayHint(tag),
    authorHint: getAuthorHint(tag),
  };
}

function getReactionTargetEventId(event: NostrEvent) {
  const eTags = event.tags.filter((tag) => tag[0] === 'e' && HEX_EVENT_ID_REGEX.test(tag[1] ?? ''));
  const markedRoot = eTags.find((tag) => tag[3] === 'root');
  const markedReply = eTags.find((tag) => tag[3] === 'reply');
  const markedMention = eTags.find((tag) => tag[3] === 'mention');
  const target = markedRoot ?? markedReply ?? markedMention ?? eTags.at(-1);

  return target?.[1]?.toLowerCase();
}

function getReactionEmoji(content: string) {
  const trimmed = content.trim();
  if (!trimmed || trimmed === '+') return '❤️';
  if (trimmed === '-') return '👎';
  if (trimmed.length <= 6) return trimmed;
  return Array.from(trimmed)[0] ?? '❤️';
}

function parseNip10ThreadReference(event: NostrEvent): ThreadReference | undefined {
  const eTags = event.tags.filter((tag) => tag[0] === 'e' && HEX_EVENT_ID_REGEX.test(tag[1] ?? ''));
  if (eTags.length === 0) return undefined;

  const markedRoot = eTags.find((tag) => tag[3] === 'root');
  const markedReply = eTags.find((tag) => tag[3] === 'reply');
  const rootTag = markedRoot ?? eTags[0];
  const replyTag = markedReply ?? eTags.at(-1);
  if (!rootTag) return undefined;

  const root = getThreadEventReference(rootTag);
  const reply = replyTag ? getThreadEventReference(replyTag) : undefined;

  return {
    root,
    reply: reply && reply.eventId !== event.id ? reply : undefined,
  };
}

function trimMapToLimit<Key, Value>(map: Map<Key, Value>, limit: number) {
  while (map.size > limit) {
    const oldestKey = map.keys().next().value as Key | undefined;
    if (oldestKey === undefined) return;
    map.delete(oldestKey);
  }
}

function getInitialRelays() {
  if (typeof window === 'undefined') return DEFAULT_RELAYS;

  const params = new URLSearchParams(window.location.search);
  const relayParams = [
    ...params.getAll('relay'),
    ...params.getAll('r'),
    ...(params.get('relays')?.split(',') ?? []),
  ];
  const normalizedRelays = relayParams
    .map(normalizeRelayUrl)
    .filter((relay) => relay.length > 0);
  const uniqueRelays = [...new Set(normalizedRelays)];

  return uniqueRelays.length > 0 ? uniqueRelays : DEFAULT_RELAYS;
}

function getMessagePreview(content: string, mentions?: MentionProfile[]) {
  const safeMentions = mentions ?? [];
  const mentionMap = new Map(safeMentions.map((mention) => [mention.pubkey, mention.mention]));
  const preview = content
    .replace(PROFILE_MENTION_REGEX, (match) => {
      const pubkey = decodeMentionPubkey(match);
      return pubkey ? mentionMap.get(pubkey) ?? '@unknown' : '@unknown';
    })
    .replace(/\s+/g, ' ')
    .trim();

  return preview || 'Empty note';
}

const Index = () => {
  const { nostr } = useNostr();
  const { config, updateConfig } = useAppContext();
  const [selectedRelays, setSelectedRelays] = useState(getInitialRelays);
  const [customRelay, setCustomRelay] = useState('');
  const [wotRootInput, setWotRootInput] = useState(config.wotRoot);
  const [wotStatus, setWotStatus] = useState<WotStatus>(config.wotRootPubkey ? 'hydrating' : 'disabled');
  const [wotMessage, setWotMessage] = useState('');
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [eventCount, setEventCount] = useState(0);
  const [zapCount, setZapCount] = useState(0);
  const [balls, setBalls] = useState<Ball[]>([]);
  const [renderTime, setRenderTime] = useState(() => performance.now());
  const [settingsCollapsed, setSettingsCollapsed] = useState(true);
  const [stageSize, setStageSize] = useState(() => ({
    width: Math.max(360, window.innerWidth),
    height: Math.max(360, window.innerHeight),
  }));

  const stageRef = useRef<HTMLDivElement | null>(null);
  const ballsRef = useRef<Ball[]>([]);
  const profileCacheRef = useRef<Map<string, ProfileResult>>(new Map());
  const mentionCacheRef = useRef<Map<string, string>>(new Map());
  const rootEventCacheRef = useRef<Map<string, NostrEvent | null>>(new Map());
  const pendingProfilesRef = useRef<Set<string>>(new Set());
  const pendingRootsRef = useRef<Set<string>>(new Set());
  const recentEventsRef = useRef<Set<string>>(new Set());
  const socialGraphRef = useRef<SocialGraph | null>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const lastFrameRef = useRef<number | undefined>(undefined);
  const spawnTimestampsRef = useRef<number[]>([]);
  const bubbleDensityConfigRef = useRef({
    mode: config.bubbleDensityMode,
    targetCount: config.bubbleTargetCount,
  });
  const zapBreaksBubblesRef = useRef(config.zapBreaksBubbles);

  useSeoMeta({
    title: 'Relay Orbit — Nostr TV Visualizer',
    description:
      'A full-screen Nostr relay visualizer that turns live kind 1 notes into bouncing profile-image planets.',
  });

  useEffect(() => {
    ballsRef.current = balls;
  }, [balls]);

  useEffect(() => {
    bubbleDensityConfigRef.current = {
      mode: config.bubbleDensityMode,
      targetCount: config.bubbleTargetCount,
    };
  }, [config.bubbleDensityMode, config.bubbleTargetCount]);

  useEffect(() => {
    zapBreaksBubblesRef.current = config.zapBreaksBubbles;
  }, [config.zapBreaksBubbles]);

  useEffect(() => {
    const updateStageSize = () => {
      setStageSize({
        width: Math.max(360, stageRef.current?.clientWidth ?? window.innerWidth),
        height: Math.max(360, stageRef.current?.clientHeight ?? window.innerHeight),
      });
    };

    updateStageSize();
    window.addEventListener('resize', updateStageSize);
    return () => window.removeEventListener('resize', updateStageSize);
  }, []);

  useEffect(() => {
    const rootPubkey = config.wotRootPubkey;
    const timers: number[] = [];

    if (!rootPubkey) {
      socialGraphRef.current = null;
      timers.push(window.setTimeout(() => {
        setWotStatus('disabled');
        setWotMessage('No WOT root selected.');
      }, 0));
      return () => timers.forEach((timer) => window.clearTimeout(timer));
    }

    const controller = new AbortController();
    socialGraphRef.current = null;
    timers.push(window.setTimeout(() => {
      setWotStatus('hydrating');
      setWotMessage('Hydrating social graph...');
    }, 0));

    const hydrateGraph = async () => {
      const cacheKey = getWotGraphCacheKey(rootPubkey);

      try {
        const cachedBinary = toCachedGraphBinary(await localforage.getItem(cacheKey));
        if (cachedBinary) {
          const graph = await SocialGraph.fromBinary(rootPubkey, cachedBinary);
          if (controller.signal.aborted) return;

          socialGraphRef.current = graph;
          setWotStatus('ready');
          setWotMessage(`Active WOT filter: ${graph.size().users.toLocaleString()} cached users loaded from ${(cachedBinary.byteLength / 1_000_000).toFixed(1)} MB.`);
          return;
        }
      } catch {
        await localforage.removeItem(cacheKey).catch(() => undefined);
      }

      try {
        const response = await fetch(SOCIAL_GRAPH_API_URL, { signal: controller.signal });
        if (!response.ok) throw new Error(`Graph API returned ${response.status}.`);

        const binary = new Uint8Array(await response.arrayBuffer());
        const graph = await SocialGraph.fromBinary(rootPubkey, binary);
        if (controller.signal.aborted) return;

        socialGraphRef.current = graph;
        setWotStatus('ready');
        setWotMessage(`Active WOT filter: ${graph.size().users.toLocaleString()} users loaded from ${(binary.byteLength / 1_000_000).toFixed(1)} MB.`);
        void graph.toBinary()
          .then((graphBinary) => localforage.setItem(cacheKey, graphBinary))
          .catch(() => undefined);
      } catch (error) {
        if (controller.signal.aborted) return;

        socialGraphRef.current = new SocialGraph(rootPubkey);
        setWotStatus('ready');
        setWotMessage(error instanceof Error ? `Graph hydrate failed; only the root user will pass: ${error.message}` : 'Graph hydrate failed; only the root user will pass.');
      }
    };

    void hydrateGraph();

    return () => {
      controller.abort();
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [config.wotRootPubkey]);

  const activeRelays = useMemo(
    () => (selectedRelays.length > 0 ? selectedRelays : DEFAULT_RELAYS),
    [selectedRelays],
  );
  const relayChoices = useMemo(() => [...new Set([...selectedRelays, ...RELAY_OPTIONS])], [selectedRelays]);
  const activeRelayLabel = activeRelays.length === 1
    ? activeRelays[0].replace('wss://', '')
    : `${activeRelays.length} relays`;
  const wotRootLabel = config.wotRootPubkey ? `${config.wotRootPubkey.slice(0, 8)}...${config.wotRootPubkey.slice(-6)}` : 'off';

  const passesWotFilter = useCallback(
    (pubkey: string | undefined) => {
      if (!config.wotRootPubkey) return true;
      if (!pubkey || !isHexPubkey(pubkey)) return false;
      if (wotStatus !== 'ready') return false;

      const graph = socialGraphRef.current;
      return Boolean(graph && graph.getFollowDistance(pubkey.toLowerCase()) <= config.wotMaxDistance);
    },
    [config.wotMaxDistance, config.wotRootPubkey, wotStatus],
  );

  const getSocialGraphRadius = useCallback(
    (pubkey: string, role: BallRole, baseRadius: number) => {
      if (!config.wotRootPubkey || wotStatus !== 'ready' || role === 'zap' || role === 'reaction') return baseRadius;
      if (!isHexPubkey(pubkey)) return baseRadius;

      const graph = socialGraphRef.current;
      const distance = graph?.getFollowDistance(pubkey.toLowerCase());
      if (distance === undefined || !Number.isFinite(distance)) return baseRadius;

      const maxDistance = Math.max(1, config.wotMaxDistance);
      const normalizedDistance = clampNumber(distance / maxDistance, 0, 1);
      const multiplier = 1.22 - normalizedDistance * 0.44;
      const radiusRange = getRoleRadiusRange(role);

      return clampNumber(baseRadius * multiplier, radiusRange[0] * 0.82, radiusRange[1] * 1.18);
    },
    [config.wotMaxDistance, config.wotRootPubkey, wotStatus],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.delete('relay');
    params.delete('r');
    params.delete('relays');
    activeRelays.forEach((relay) => params.append('relay', relay));
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', nextUrl);
  }, [activeRelays]);

  const toggleRelay = (relay: string) => {
    setSelectedRelays((currentRelays) => {
      if (currentRelays.includes(relay)) {
        return currentRelays.length > 1 ? currentRelays.filter((selectedRelay) => selectedRelay !== relay) : currentRelays;
      }

      return [...currentRelays, relay];
    });
  };

  const spawnBall = useCallback((event: NostrEvent, imageUrl: string, shape: AvatarShape | undefined, messagePreview: string, options: SpawnOptions = {}) => {
    const width = Math.max(360, stageRef.current?.clientWidth ?? window.innerWidth);
    const height = Math.max(360, stageRef.current?.clientHeight ?? window.innerHeight);
    const role = options.role ?? 'note';
    const radiusRange = getRoleRadiusRange(role);
    const baseRadius = options.radius ?? randomBetween(radiusRange[0], radiusRange[1]);
    const generatedRadius = options.radius ?? getSocialGraphRadius(event.pubkey, role, baseRadius);
    const radius = options.maxRadius === undefined ? generatedRadius : Math.min(generatedRadius, options.maxRadius);
    const speed = role === 'root' ? randomBetween(36, 96) : role === 'zap' ? randomBetween(620, 1_040) : role === 'reaction' ? randomBetween(360, 720) : randomBetween(70, 210);
    const angle = role === 'zap' ? randomBetween(-Math.PI * 0.32, Math.PI * 0.32) : role === 'reaction' ? randomBetween(-Math.PI * 0.11, Math.PI * 0.11) : randomBetween(0, Math.PI * 2);
    const now = performance.now();
    const densityConfig = bubbleDensityConfigRef.current;
    const targetCount = densityConfig.mode === 'auto'
      ? getAutoBubbleTarget(width, height)
      : clampNumber(densityConfig.targetCount, MANUAL_BUBBLE_TARGET_MIN, MANUAL_BUBBLE_TARGET_MAX);
    spawnTimestampsRef.current = [...spawnTimestampsRef.current, now]
      .filter((timestamp) => now - timestamp <= BUBBLE_SPAWN_RATE_WINDOW);
    const spawnRatePerSecond = spawnTimestampsRef.current.length / (BUBBLE_SPAWN_RATE_WINDOW / 1000);
    const lifetime = getBubbleLifetime(role, targetCount, spawnRatePerSecond) * randomBetween(0.88, 1.12);
    const maxBalls = Math.min(MAX_BALLS, targetCount + 12);
    const stageArea = width * height;
    const incomingPersistentArea = isPersistentBallRole(role) ? getDiscArea(radius) : 0;

    setBalls((currentBalls) => {
      if (options.reuseExisting) {
        const existing = currentBalls.find((ball) => ball.eventId === event.id && ball.role === role);
        if (existing) {
          const extended = currentBalls.map((ball) => (
            ball.id === existing.id
              ? {
                  ...ball,
                  expiresAt: Math.max(ball.expiresAt, now + lifetime),
                  messagePreview,
                  imageUrl,
                  shape,
                  radius: options.maxRadius === undefined ? ball.radius : Math.min(ball.radius, options.maxRadius),
                }
              : ball
          ));
          ballsRef.current = extended;
          return extended;
        }
      }

      const rootBalls = currentBalls.filter((ball) => ball.role === 'root');
      const prunedForRootLimit = role === 'root' && rootBalls.length >= MAX_ROOT_BALLS
        ? currentBalls.filter((ball) => ball.id !== rootBalls[0]?.id)
        : currentBalls;
      const prunedForArea = incomingPersistentArea > 0
        ? pruneForBubbleArea(prunedForRootLimit, incomingPersistentArea, stageArea)
        : prunedForRootLimit;
      const startsFromLeft = Math.random() > 0.5;
      const zapX = startsFromLeft ? -radius * randomBetween(2.6, 6) : width + radius * randomBetween(2.6, 6);
      const zapY = randomBetween(radius + 96, Math.max(radius + 96, height - radius));
      const zapAngle = startsFromLeft ? angle : Math.PI - angle;
      const reactionX = startsFromLeft ? -radius * randomBetween(3.2, 8) : width + radius * randomBetween(3.2, 8);
      const reactionY = randomBetween(radius + 112, Math.max(radius + 112, height - radius - 40));
      const reactionAngle = startsFromLeft ? angle : Math.PI - angle;
      const nextBall: Ball = {
        id: `${event.id}-${role}-${now}-${Math.random().toString(36).slice(2)}`,
        eventId: event.id,
        pubkey: event.pubkey,
        imageUrl,
        shape,
        messagePreview,
        x: role === 'zap' ? zapX : role === 'reaction' ? reactionX : randomBetween(radius, Math.max(radius, width - radius)),
        y: role === 'zap' ? zapY : role === 'reaction' ? reactionY : randomBetween(radius + 96, Math.max(radius + 96, height - radius)),
        vx: Math.cos(role === 'reaction' ? reactionAngle : zapAngle) * speed,
        vy: Math.sin(role === 'reaction' ? reactionAngle : zapAngle) * speed,
        radius,
        createdAt: now,
        expiresAt: now + lifetime,
        hue: role === 'root' ? randomBetween(34, 62) : role === 'zap' ? randomBetween(42, 55) : role === 'reaction' ? randomBetween(280, 340) : randomBetween(170, 315),
        role,
        rootEventId: options.rootEventId,
        replyEventId: options.replyEventId,
        zapAmountSats: options.zapAmountSats,
        trail: role === 'zap' ? [{ x: zapX, y: zapY, time: now }] : undefined,
        enteredViewport: role === 'zap' ? false : undefined,
        targetSpeed: role === 'zap' ? speed : undefined,
      };
      const nextBalls = [
        ...prunedForArea.slice(Math.max(0, prunedForArea.length - maxBalls + 1)),
        nextBall,
      ];
      ballsRef.current = nextBalls;
      return nextBalls;
    });
  }, [getSocialGraphRadius]);

  const resolveMentionProfiles = useCallback(
    async (event: NostrEvent, signal: AbortSignal) => {
      const mentionProfiles = getMentionProfiles(event.content).map((mention) => ({
        ...mention,
        mention: mentionCacheRef.current.get(mention.pubkey) ?? '@unknown',
      }));
      const mentionedPubkeys = mentionProfiles
        .map((mention) => mention.pubkey)
        .filter((pubkey) => !mentionCacheRef.current.has(pubkey));

      if (mentionedPubkeys.length > 0) {
        try {
          const profileResults = await Promise.allSettled(
            activeRelays.map((relayUrl) => {
              const relay = nostr.relay(relayUrl);
              return relay.query(
                [{ kinds: [0], authors: mentionedPubkeys, limit: mentionedPubkeys.length }],
                { signal },
              );
            }),
          );
          const profiles = profileResults.flatMap((result) => result.status === 'fulfilled' ? result.value : []);

          for (const pubkey of mentionedPubkeys) {
            const profile = profiles.find((candidate) => candidate.pubkey === pubkey);
            const displayName = getDisplayName(profile);
            mentionCacheRef.current.set(pubkey, displayName ? `@${displayName}` : '@unknown');
          }
        } catch {
          for (const pubkey of mentionedPubkeys) {
            mentionCacheRef.current.set(pubkey, '@unknown');
          }
        }
      }

      return mentionProfiles.map((mention) => ({
        ...mention,
        mention: mentionCacheRef.current.get(mention.pubkey) ?? mention.mention,
      }));
    },
    [activeRelays, nostr],
  );

  const fetchProfileAndSpawn = useCallback(
    async (event: NostrEvent, signal: AbortSignal, options: SpawnOptions = {}) => {
      const cached = profileCacheRef.current.get(event.pubkey);
      const resolvedMentions = options.role === 'zap' ? [] : await resolveMentionProfiles(event, signal);
      const messagePreview = options.role === 'zap'
        ? `${options.zapAmountSats ? formatSats(options.zapAmountSats) : 'mystery'} sats`
        : getMessagePreview(event.content, resolvedMentions);

      if (cached) {
        const imageUrl = options.role === 'zap' ? await preloadImage(cached.imageUrl, signal) : cached.imageUrl;
        if (!signal.aborted) spawnBall(event, imageUrl, cached.shape, messagePreview, options);
        return;
      }

      if (pendingProfilesRef.current.has(event.pubkey) && options.role !== 'zap' && !options.reuseExisting) return;
      pendingProfilesRef.current.add(event.pubkey);

      try {
        const profileResults = await Promise.allSettled(
          activeRelays.map((relayUrl) => {
            const relay = nostr.relay(relayUrl);
            return relay.query(
              [{ kinds: [0], authors: [event.pubkey], limit: 1 }],
              { signal },
            );
          }),
        );
        const profiles = profileResults.flatMap((result) => result.status === 'fulfilled' ? result.value : []);
        const imageUrl = getProfileImage(profiles[0]);
        const shape = getProfileShape(profiles[0]);
        profileCacheRef.current.set(event.pubkey, { imageUrl, shape });
        trimMapToLimit(profileCacheRef.current, PROFILE_CACHE_LIMIT);

        const readyImageUrl = options.role === 'zap' ? await preloadImage(imageUrl, signal) : imageUrl;
        if (!signal.aborted) spawnBall(event, readyImageUrl, shape, messagePreview, options);
      } catch {
        if (!signal.aborted) {
          const imageUrl = DEFAULT_PROFILE_IMAGE;
          profileCacheRef.current.set(event.pubkey, { imageUrl });
          trimMapToLimit(profileCacheRef.current, PROFILE_CACHE_LIMIT);
          spawnBall(event, imageUrl, undefined, messagePreview, options);
        }
      } finally {
        pendingProfilesRef.current.delete(event.pubkey);
      }
    },
    [activeRelays, nostr, resolveMentionProfiles, spawnBall],
  );

  const fetchReferencedNoteAndSpawn = useCallback(
    async (reference: ThreadEventReference, signal: AbortSignal, options: SpawnOptions) => {
      const cached = rootEventCacheRef.current.get(reference.eventId);
      if (cached) {
        void fetchProfileAndSpawn(cached, signal, { ...options, reuseExisting: true });
        return;
      }
      if (cached === null || pendingRootsRef.current.has(reference.eventId)) return;

      pendingRootsRef.current.add(reference.eventId);

      try {
        const relayUrls = [
          ...activeRelays,
          ...(reference.relayHint && !activeRelays.includes(reference.relayHint) ? [reference.relayHint] : []),
        ];
        let referencedEvent: NostrEvent | undefined;

        for (const relayUrl of relayUrls) {
          if (signal.aborted) return;
          try {
            const relay = nostr.relay(relayUrl);
            const [candidate] = await relay.query(
              [{
                kinds: [1],
                ids: [reference.eventId],
                ...(reference.authorHint ? { authors: [reference.authorHint] } : {}),
                limit: 1,
              }],
              { signal },
            );
            if (candidate) {
              referencedEvent = candidate;
              break;
            }
          } catch {
            // Try the next hint; some relay hints are stale or offline.
          }
        }

        rootEventCacheRef.current.set(reference.eventId, referencedEvent ?? null);
        trimMapToLimit(rootEventCacheRef.current, ROOT_CACHE_LIMIT);

        if (referencedEvent && !signal.aborted && passesWotFilter(referencedEvent.pubkey)) {
          void fetchProfileAndSpawn(referencedEvent, signal, { ...options, reuseExisting: true });
        }
      } finally {
        pendingRootsRef.current.delete(reference.eventId);
      }
    },
    [activeRelays, fetchProfileAndSpawn, nostr, passesWotFilter],
  );

  const handleIncomingEvent = useCallback(
    (event: NostrEvent, signal: AbortSignal) => {
      if (event.kind !== 1 || recentEventsRef.current.has(event.id)) return;
      if (!passesWotFilter(event.pubkey)) return;
      recentEventsRef.current.add(event.id);
      if (recentEventsRef.current.size > 600) {
        const oldestId = recentEventsRef.current.keys().next().value;
        if (typeof oldestId === 'string') recentEventsRef.current.delete(oldestId);
      }

      setEventCount((count) => count + 1);
      const threadReference = parseNip10ThreadReference(event);
      const role = threadReference ? 'reply' : 'note';
      const radiusRange = getRoleRadiusRange(role);
      const radius = getSocialGraphRadius(event.pubkey, role, randomBetween(radiusRange[0], radiusRange[1]));
      const rootEventId = threadReference?.root.eventId;
      const replyEventId = threadReference?.reply?.eventId;

      void fetchProfileAndSpawn(event, signal, {
        role,
        rootEventId,
        replyEventId,
        radius,
      });

      if (threadReference && rootEventId && rootEventId !== event.id) {
        void fetchReferencedNoteAndSpawn(threadReference.root, signal, {
          role: 'root',
          maxRadius: radius,
        });
      }

      if (threadReference?.reply && replyEventId !== event.id && replyEventId !== rootEventId) {
        void fetchReferencedNoteAndSpawn(threadReference.reply, signal, {
          role: 'reply',
          rootEventId,
          maxRadius: radius,
        });
      }
    },
    [fetchProfileAndSpawn, fetchReferencedNoteAndSpawn, getSocialGraphRadius, passesWotFilter],
  );

  const handleIncomingReaction = useCallback(
    (event: NostrEvent, signal: AbortSignal) => {
      if (event.kind !== 7 || recentEventsRef.current.has(event.id)) return;

      const targetEventId = getReactionTargetEventId(event);
      if (!targetEventId) return;
      if (!passesWotFilter(event.pubkey)) return;

      recentEventsRef.current.add(event.id);
      if (recentEventsRef.current.size > 600) {
        const oldestId = recentEventsRef.current.keys().next().value;
        if (typeof oldestId === 'string') recentEventsRef.current.delete(oldestId);
      }

      const hasVisibleTarget = ballsRef.current.some((ball) => ball.eventId === targetEventId && ball.role !== 'reaction' && ball.role !== 'zap');
      if (!hasVisibleTarget) return;

      void fetchProfileAndSpawn(
        { ...event, content: getReactionEmoji(event.content) },
        signal,
        { role: 'reaction', rootEventId: targetEventId },
      );
    },
    [fetchProfileAndSpawn, passesWotFilter],
  );

  const handleIncomingZap = useCallback(
    (event: NostrEvent, signal: AbortSignal) => {
      if (event.kind !== 9735 || recentEventsRef.current.has(event.id)) return;

      const senderPubkey = getZapSenderPubkey(event);
      const zapAmountSats = getZapAmountSats(event);
      if (!senderPubkey || !zapAmountSats) return;
      if (!passesWotFilter(senderPubkey)) return;

      recentEventsRef.current.add(event.id);
      if (recentEventsRef.current.size > 600) {
        const oldestId = recentEventsRef.current.keys().next().value;
        if (typeof oldestId === 'string') recentEventsRef.current.delete(oldestId);
      }

      setZapCount((count) => count + 1);
      void fetchProfileAndSpawn(
        { ...event, pubkey: senderPubkey, content: '' },
        signal,
        { role: 'zap', zapAmountSats },
      );
    },
    [fetchProfileAndSpawn, passesWotFilter],
  );

  useEffect(() => {
    const controller = new AbortController();
    const reconnectTimers: number[] = [];
    const resetTimer = window.setTimeout(() => {
      setEventCount(0);
      setZapCount(0);
      setBalls([]);
      spawnTimestampsRef.current = [];
    }, 0);
    const relayRetries = new Map(activeRelays.map((relayUrl) => [relayUrl, 0]));
    const liveRelays = new Set<string>();

    const subscribe = async (relayUrl: string) => {
      const retry = relayRetries.get(relayUrl) ?? 0;
      setConnectionState(liveRelays.size > 0 ? 'live' : retry === 0 ? 'connecting' : 'reconnecting');
      try {
        const relay = nostr.relay(relayUrl);
        const since = Math.floor(Date.now() / 1000) - 8;
        const kinds = [1, 7, 9735];

        for await (const msg of relay.req([{ kinds, since, limit: 40 }], {
          signal: controller.signal,
        })) {
          if (controller.signal.aborted) return;
          liveRelays.add(relayUrl);
          relayRetries.set(relayUrl, 0);
          setConnectionState('live');

          if (msg[0] === 'EVENT') {
            const event = msg[2];
            if (event.kind === 9735) {
              handleIncomingZap(event, controller.signal);
            } else if (event.kind === 7) {
              handleIncomingReaction(event, controller.signal);
            } else {
              handleIncomingEvent(event, controller.signal);
            }
          }
        }
      } catch {
        if (controller.signal.aborted) return;
        liveRelays.delete(relayUrl);
        setConnectionState(liveRelays.size > 0 ? 'live' : 'offline');
      }

      if (!controller.signal.aborted) {
        const nextRetry = (relayRetries.get(relayUrl) ?? 0) + 1;
        relayRetries.set(relayUrl, nextRetry);
        const delay = Math.min(10_000, 900 + nextRetry * 850);
        reconnectTimers.push(window.setTimeout(() => void subscribe(relayUrl), delay));
      }
    };

    recentEventsRef.current.clear();
    pendingProfilesRef.current.clear();
    pendingRootsRef.current.clear();
    rootEventCacheRef.current.clear();
    ballsRef.current = [];
    spawnTimestampsRef.current = [];
    activeRelays.forEach((relayUrl) => void subscribe(relayUrl));

    return () => {
      controller.abort();
      window.clearTimeout(resetTimer);
      reconnectTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [activeRelays, handleIncomingEvent, handleIncomingReaction, handleIncomingZap, nostr]);

  useEffect(() => {
    const animate = (time: number) => {
      const previous = lastFrameRef.current ?? time;
      const delta = Math.min(0.032, (time - previous) / 1000);
      lastFrameRef.current = time;

      const width = Math.max(360, stageRef.current?.clientWidth ?? window.innerWidth);
      const height = Math.max(360, stageRef.current?.clientHeight ?? window.innerHeight);
      const updated = ballsRef.current
        .filter((ball) => ball.expiresAt > time)
        .map((ball) => ({ ...ball, trail: ball.trail ? [...ball.trail] : undefined }));

      for (const ball of updated) {
        ball.x += ball.vx * delta;
        ball.y += ball.vy * delta;

        if (ball.role === 'reaction') {
          const offscreenPadding = Math.max(180, ball.radius * 8);
          if (
            ball.x < -offscreenPadding
            || ball.x > width + offscreenPadding
            || ball.y < -offscreenPadding
            || ball.y > height + offscreenPadding
          ) {
            ball.expiresAt = time;
          }
          continue;
        }

        if (ball.role === 'zap') {
          const isInsideViewport = ball.x - ball.radius >= 0 && ball.x + ball.radius <= width && ball.y - ball.radius >= 0 && ball.y + ball.radius <= height;
          ball.enteredViewport = ball.enteredViewport || isInsideViewport;

          if (ball.enteredViewport) {
            if (ball.x - ball.radius < 0) {
              ball.x = ball.radius;
              ball.vx = Math.abs(ball.vx);
            } else if (ball.x + ball.radius > width) {
              ball.x = width - ball.radius;
              ball.vx = -Math.abs(ball.vx);
            }

            if (ball.y - ball.radius < 0) {
              ball.y = ball.radius;
              ball.vy = Math.abs(ball.vy);
            } else if (ball.y + ball.radius > height) {
              ball.y = height - ball.radius;
              ball.vy = -Math.abs(ball.vy);
            }
          }

          if (ball.targetSpeed && ball.enteredViewport) {
            const currentSpeed = Math.hypot(ball.vx, ball.vy);
            if (currentSpeed > 0.1 && currentSpeed < ball.targetSpeed) {
              const factor = 1 - Math.exp(-0.5 * delta);
              const nextSpeed = currentSpeed + (ball.targetSpeed - currentSpeed) * factor;
              const scale = nextSpeed / currentSpeed;
              ball.vx *= scale;
              ball.vy *= scale;
            }
          }

          ball.trail = [...(ball.trail ?? []), { x: ball.x, y: ball.y, time }]
            .filter((point) => time - point.time <= ZAP_TRAIL_DURATION)
            .slice(-34);
          continue;
        }

        if (ball.x - ball.radius < 0) {
          ball.x = ball.radius;
          ball.vx = Math.abs(ball.vx);
        } else if (ball.x + ball.radius > width) {
          ball.x = width - ball.radius;
          ball.vx = -Math.abs(ball.vx);
        }

        if (ball.y - ball.radius < 0) {
          ball.y = ball.radius;
          ball.vy = Math.abs(ball.vy);
        } else if (ball.y + ball.radius > height) {
          ball.y = height - ball.radius;
          ball.vy = -Math.abs(ball.vy);
        }
      }

      for (let i = 0; i < updated.length; i += 1) {
        for (let j = i + 1; j < updated.length; j += 1) {
          const a = updated[i];
          const b = updated[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const distance = Math.hypot(dx, dy) || 0.001;
          const minDistance = a.radius + b.radius;

          if (a.role === 'reaction' || b.role === 'reaction') continue;
          if (a.explodingAt !== undefined || b.explodingAt !== undefined) continue;

          if (distance < minDistance) {
            const nx = dx / distance;
            const ny = dy / distance;
            const overlap = minDistance - distance;
            const totalMass = a.radius * a.radius + b.radius * b.radius;
            const aShare = a.role === 'zap' ? 0 : (b.radius * b.radius) / totalMass;
            const bShare = b.role === 'zap' ? 0 : (a.radius * a.radius) / totalMass;

            a.x -= nx * overlap * aShare;
            a.y -= ny * overlap * aShare;
            b.x += nx * overlap * bShare;
            b.y += ny * overlap * bShare;

            const dvx = b.vx - a.vx;
            const dvy = b.vy - a.vy;
            const impact = dvx * nx + dvy * ny;

            if (impact < 0) {
              const restitution = a.role === 'zap' || b.role === 'zap' ? 1.08 : 0.96;
              const aMass = a.radius * a.radius;
              const bMass = b.radius * b.radius;
              const impulse = (-(1 + restitution) * impact) / (1 / aMass + 1 / bMass);

              if (zapBreaksBubblesRef.current) {
                const zap = a.role === 'zap' ? a : b.role === 'zap' ? b : null;
                const target = zap === a ? b : zap === b ? a : null;
                if (zap && target && (target.role === 'note' || target.role === 'reply' || target.role === 'root')) {
                  // Only crack the bubble if the zap is at or above ~90% of its target speed.
                  // A collision-slowed comet must re-accelerate before it can break things again.
                  // Going faster than targetSpeed (e.g. boosted by another bubble) also counts.
                  const zapSpeed = Math.hypot(zap.vx, zap.vy);
                  const atFullSpeed = zap.targetSpeed != null && zapSpeed >= zap.targetSpeed * 0.9;
                  if (atFullSpeed) {
                    const nextCracks = (target.cracks ?? 0) + 1;
                    target.cracks = nextCracks;
                    if (nextCracks >= MAX_CRACKS) {
                      target.explodingAt = time;
                      target.expiresAt = time + EXPLOSION_DURATION;
                      target.vx *= 0.18;
                      target.vy *= 0.18;
                    }
                  }
                }
              }

              a.vx -= (impulse * nx) / aMass;
              a.vy -= (impulse * ny) / aMass;
              b.vx += (impulse * nx) / bMass;
              b.vy += (impulse * ny) / bMass;
            }
          }
        }
      }

      ballsRef.current = updated;
      setRenderTime(time);
      setBalls(updated);
      animationFrameRef.current = window.requestAnimationFrame(animate);
    };

    animationFrameRef.current = window.requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) window.cancelAnimationFrame(animationFrameRef.current);
      lastFrameRef.current = undefined;
    };
  }, []);

  const applyCustomRelay = () => {
    const normalized = normalizeRelayUrl(customRelay);
    if (normalized) {
      setSelectedRelays((currentRelays) => (
        currentRelays.includes(normalized) ? currentRelays : [...currentRelays, normalized]
      ));
      setCustomRelay('');
    }
  };

  const applyWotRoot = async () => {
    const root = wotRootInput.trim();
    if (!root) {
      updateConfig((currentConfig) => ({ ...currentConfig, wotRoot: '', wotRootPubkey: undefined }));
      return;
    }

    const controller = new AbortController();
    setWotStatus('resolving');
    setWotMessage('Resolving WOT root...');

    try {
      const pubkey = await resolveWotRootPubkey(root, controller.signal);
      updateConfig((currentConfig) => ({ ...currentConfig, wotRoot: root, wotRootPubkey: pubkey }));
      setWotMessage('Root resolved. Hydrating social graph...');
    } catch (error) {
      setWotStatus('error');
      setWotMessage(error instanceof Error ? error.message : 'Could not resolve WOT root.');
    }
  };

  const clearWotRoot = () => {
    setWotRootInput('');
    updateConfig((currentConfig) => ({ ...currentConfig, wotRoot: '', wotRootPubkey: undefined }));
  };

  const statusTone = {
    connecting: 'border-amber-300/40 bg-amber-300/15 text-amber-100',
    live: 'border-emerald-300/40 bg-emerald-300/15 text-emerald-100',
    reconnecting: 'border-sky-300/40 bg-sky-300/15 text-sky-100',
    offline: 'border-rose-300/40 bg-rose-300/15 text-rose-100',
  } satisfies Record<ConnectionState, string>;

  const currentBubbleTarget = config.bubbleDensityMode === 'auto'
    ? getAutoBubbleTarget(stageSize.width, stageSize.height)
    : config.bubbleTargetCount;

  const threadLinks = useMemo<ThreadLink[]>(() => {
    const rootByEventId = new Map<string, Ball>();
    const relatedByEventId = new Map<string, Ball>();
    for (const ball of balls) {
      if (ball.role === 'root') rootByEventId.set(ball.eventId, ball);
      if (ball.role !== 'zap' && ball.role !== 'reaction') relatedByEventId.set(ball.eventId, ball);
    }

    return balls.flatMap((ball) => {
      if (ball.role !== 'reply') return [];

      const links: ThreadLink[] = [];
      const root = ball.rootEventId ? rootByEventId.get(ball.rootEventId) : undefined;
      const replyTarget = ball.replyEventId && ball.replyEventId !== ball.rootEventId
        ? relatedByEventId.get(ball.replyEventId)
        : undefined;

      if (root && root.id !== ball.id) {
        links.push({ id: `${root.id}-${ball.id}-root`, from: root, to: ball, relation: 'root' });
      }

      if (replyTarget && replyTarget.id !== ball.id) {
        links.push({ id: `${replyTarget.id}-${ball.id}-reply`, from: replyTarget, to: ball, relation: 'reply' });
      }

      return links;
    });
  }, [balls]);

  return (
    <main
      ref={stageRef}
      className="relative h-screen w-screen overflow-hidden bg-[#02030a] text-white isolate"
    >
      <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_18%_22%,rgba(45,212,191,0.22),transparent_30%),radial-gradient(circle_at_78%_18%,rgba(168,85,247,0.24),transparent_28%),radial-gradient(circle_at_52%_80%,rgba(14,165,233,0.18),transparent_34%),linear-gradient(135deg,#02030a_0%,#070717_48%,#010106_100%)]" />
      <div className="absolute inset-0 -z-10 opacity-30 [background-image:radial-gradient(circle_at_center,rgba(255,255,255,.4)_0_1px,transparent_1px)] [background-size:34px_34px]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-black/65 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/70 to-transparent" />

      <section
        className={`absolute left-4 right-4 top-4 z-20 rounded-3xl border border-white/10 bg-black/35 p-4 shadow-2xl shadow-cyan-950/40 backdrop-blur-xl transition-all duration-300 ease-out sm:left-6 sm:right-6 lg:left-8 lg:right-auto ${settingsCollapsed ? 'lg:w-[360px]' : 'lg:w-[680px]'}`}
        aria-label="Relay visualizer settings"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <button
              type="button"
              onClick={() => setSettingsCollapsed((collapsed) => !collapsed)}
              className="group min-w-0 flex-1 rounded-2xl text-left transition hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
              aria-expanded={!settingsCollapsed}
              aria-controls="relay-settings-panel"
            >
              <div className="p-2 -m-2">
                <div className="mb-1 flex items-center gap-2 text-sm font-medium uppercase tracking-[0.24em] text-cyan-200/80">
                  <Radio className="h-4 w-4" /> Relay Orbit
                </div>
                <h1 className={`truncate font-black tracking-tight ${settingsCollapsed ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-4xl'}`}>
                  Live Nostr void
                </h1>
              </div>
            </button>
            <div className="flex shrink-0 items-center gap-2">
              <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${statusTone[connectionState]}`}>
                {connectionState === 'offline' ? <WifiOff className="h-4 w-4" /> : <Wifi className="h-4 w-4" />}
                {connectionState}
              </div>
              {!settingsCollapsed && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setSettingsCollapsed(true)}
                  aria-expanded={!settingsCollapsed}
                  aria-controls="relay-settings-panel"
                  aria-label="Collapse settings"
                  className="rounded-full border border-white/10 bg-white/10 text-white hover:bg-white/20 hover:text-white focus-visible:ring-cyan-300"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div
            id="relay-settings-panel"
            className={`grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-out ${settingsCollapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'}`}
            aria-hidden={settingsCollapsed}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="flex flex-col gap-4 pt-1">
                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <div className="space-y-2">
                    <Label id="relay-select-label" className="text-white/80">Watch relays</Label>
                    <div
                      className="rounded-2xl border border-white/15 bg-white/10 p-2 shadow-inner shadow-black/20"
                      role="group"
                      aria-labelledby="relay-select-label"
                    >
                      <div className="mb-2 rounded-xl bg-black/25 px-3 py-2 text-sm font-semibold text-cyan-100">
                        Watching {activeRelayLabel}
                      </div>
                      <div className="max-h-56 space-y-1 overflow-y-auto pr-1">
                        {relayChoices.map((relay) => {
                          const isSelected = selectedRelays.includes(relay);
                          const isOnlySelected = isSelected && selectedRelays.length === 1;

                          return (
                            <label
                              key={relay}
                              className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm text-white/86 transition hover:bg-white/10"
                            >
                              <Checkbox
                                checked={isSelected}
                                disabled={isOnlySelected}
                                onCheckedChange={() => toggleRelay(relay)}
                                className="border-white/35 bg-black/20 data-[state=checked]:border-cyan-200 data-[state=checked]:bg-cyan-300 data-[state=checked]:text-slate-950"
                                tabIndex={settingsCollapsed ? -1 : undefined}
                                aria-label={`${isSelected ? 'Stop watching' : 'Watch'} ${relay}`}
                              />
                              <span className="min-w-0 truncate">{relay.replace('wss://', '')}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    <p className="text-xs text-white/52">Select multiple relays to merge live notes and zaps into one orbit.</p>
                  </div>
                  <div className="grid grid-cols-4 gap-2 rounded-2xl border border-white/10 bg-white/10 p-3 text-center sm:w-72">
                    <div>
                      <div className="text-2xl font-black tabular-nums">{eventCount}</div>
                      <div className="text-xs uppercase tracking-wider text-white/60">notes</div>
                    </div>
                    <div>
                      <div className="text-2xl font-black tabular-nums text-yellow-200">{zapCount}</div>
                      <div className="text-xs uppercase tracking-wider text-white/60">zaps</div>
                    </div>
                    <div>
                      <div className="text-2xl font-black tabular-nums text-indigo-100">{balls.filter((ball) => ball.role === 'reaction').length}</div>
                      <div className="text-xs uppercase tracking-wider text-white/60">reacts</div>
                    </div>
                    <div>
                      <div className="text-2xl font-black tabular-nums">{balls.length}</div>
                      <div className="text-xs uppercase tracking-wider text-white/60">orbs</div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 rounded-2xl border border-cyan-200/15 bg-cyan-300/5 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Label id="bubble-density-label" className="font-bold text-cyan-100">Bubble density</Label>
                      <p className="mt-1 text-xs leading-relaxed text-white/55">
                        Tune how long bubbles stay visible so slow feeds still feel alive and fast feeds stay readable.
                      </p>
                    </div>
                    <div className="rounded-full border border-cyan-100/20 bg-black/24 px-2.5 py-1 text-xs font-black uppercase tracking-wider text-cyan-100">
                      {config.bubbleDensityMode === 'auto' ? 'auto' : 'manual'} {currentBubbleTarget}
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <label className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-1.5 text-sm text-white/86 transition hover:bg-white/10">
                      <Checkbox
                        checked={config.bubbleDensityMode === 'auto'}
                        onCheckedChange={(checked) => {
                          updateConfig((currentConfig) => ({
                            ...currentConfig,
                            bubbleDensityMode: checked === true ? 'auto' : 'manual',
                          }));
                        }}
                        className="border-white/35 bg-black/20 data-[state=checked]:border-cyan-200 data-[state=checked]:bg-cyan-300 data-[state=checked]:text-slate-950"
                        tabIndex={settingsCollapsed ? -1 : undefined}
                        aria-labelledby="bubble-density-label"
                      />
                      <span>Auto-adjust target count</span>
                    </label>
                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                      <Label htmlFor="bubble-target-count" className="text-sm text-white/78">Target bubbles</Label>
                      <Input
                        id="bubble-target-count"
                        type="number"
                        min={MANUAL_BUBBLE_TARGET_MIN}
                        max={MANUAL_BUBBLE_TARGET_MAX}
                        value={config.bubbleTargetCount}
                        disabled={config.bubbleDensityMode === 'auto'}
                        onChange={(event) => {
                          const value = Number.parseInt(event.target.value, 10);
                          if (Number.isFinite(value)) {
                            updateConfig((currentConfig) => ({
                              ...currentConfig,
                              bubbleTargetCount: Math.round(clampNumber(value, MANUAL_BUBBLE_TARGET_MIN, MANUAL_BUBBLE_TARGET_MAX)),
                            }));
                          }
                        }}
                        className="w-24 border-white/15 bg-white/10 text-white disabled:cursor-not-allowed disabled:opacity-45 focus-visible:ring-cyan-300/50"
                        tabIndex={settingsCollapsed ? -1 : undefined}
                      />
                    </div>
                  </div>
                  <p className="text-xs leading-relaxed text-white/55">
                    {config.bubbleDensityMode === 'auto'
                      ? `Auto target is ${currentBubbleTarget} bubbles for this screen size.`
                      : `Manual target keeps roughly ${currentBubbleTarget} bubbles visible as relay speed changes.`}
                  </p>
                  <label className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-1.5 text-sm text-white/86 transition hover:bg-white/10">
                    <Checkbox
                      checked={config.zapBreaksBubbles}
                      onCheckedChange={(checked) => {
                        updateConfig((currentConfig) => ({
                          ...currentConfig,
                          zapBreaksBubbles: checked === true,
                        }));
                      }}
                      className="border-white/35 bg-black/20 data-[state=checked]:border-yellow-200 data-[state=checked]:bg-yellow-300 data-[state=checked]:text-slate-950"
                      tabIndex={settingsCollapsed ? -1 : undefined}
                      aria-label="Zap comets break note bubbles on impact"
                    />
                    <span>Zap comets break note bubbles</span>
                  </label>
                </div>

                <div className="grid gap-3 rounded-2xl border border-emerald-200/15 bg-emerald-300/5 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Label htmlFor="wot-root" className="font-bold text-emerald-100">Web of Trust root</Label>
                      <p className="mt-1 text-xs leading-relaxed text-white/55">
                        Set a root npub, NIP-05, or hex pubkey to filter incoming events by follow distance.
                      </p>
                    </div>
                    <div className="rounded-full border border-emerald-100/20 bg-black/24 px-2.5 py-1 text-xs font-black uppercase tracking-wider text-emerald-100">
                      {wotRootLabel}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      id="wot-root"
                      list="wot-root-options"
                      value={wotRootInput}
                      onChange={(event) => setWotRootInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') void applyWotRoot();
                      }}
                      placeholder="npub1..., name@example.com, or hex pubkey"
                      aria-label="Web of Trust root"
                      className="border-white/15 bg-white/10 text-white placeholder:text-white/45 focus-visible:ring-emerald-300/50"
                      tabIndex={settingsCollapsed ? -1 : undefined}
                    />
                    <datalist id="wot-root-options">
                      {WOT_ROOT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value} label={option.label} />
                      ))}
                    </datalist>
                    <Button
                      type="button"
                      onClick={() => void applyWotRoot()}
                      className="bg-emerald-300 text-slate-950 hover:bg-emerald-200"
                      tabIndex={settingsCollapsed ? -1 : undefined}
                    >
                      Set root
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={clearWotRoot}
                      className="border border-white/10 bg-white/5 text-white hover:bg-white/12 hover:text-white"
                      tabIndex={settingsCollapsed ? -1 : undefined}
                    >
                      Clear
                    </Button>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <Label htmlFor="wot-distance" className="text-sm text-white/78">Maximum follow distance</Label>
                    <Input
                      id="wot-distance"
                      type="number"
                      min={0}
                      max={6}
                      value={config.wotMaxDistance}
                      onChange={(event) => {
                        const value = Number.parseInt(event.target.value, 10);
                        if (Number.isFinite(value)) {
                          updateConfig((currentConfig) => ({
                            ...currentConfig,
                            wotMaxDistance: Math.min(6, Math.max(0, value)),
                          }));
                        }
                      }}
                      className="w-24 border-white/15 bg-white/10 text-white focus-visible:ring-emerald-300/50"
                      tabIndex={settingsCollapsed ? -1 : undefined}
                    />
                  </div>
                  <p className={`text-xs leading-relaxed ${wotStatus === 'error' ? 'text-rose-200' : wotStatus === 'ready' ? 'text-emerald-100/82' : 'text-white/55'}`}>
                    {wotMessage || 'No WOT root selected.'}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    value={customRelay}
                    onChange={(event) => setCustomRelay(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') applyCustomRelay();
                    }}
                    placeholder="custom relay, e.g. relay.example.com"
                    aria-label="Custom relay URL"
                    className="border-white/15 bg-white/10 text-white placeholder:text-white/45 focus-visible:ring-cyan-300/50"
                    tabIndex={settingsCollapsed ? -1 : undefined}
                  />
                  <Button
                    type="button"
                    onClick={applyCustomRelay}
                    className="bg-cyan-300 text-slate-950 hover:bg-cyan-200"
                    tabIndex={settingsCollapsed ? -1 : undefined}
                  >
                    Tune in
                  </Button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      <svg className="pointer-events-none absolute inset-0 z-0 h-full w-full overflow-visible" aria-hidden="true">
        <defs>
          <linearGradient id="thread-string-gradient" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="rgba(251,191,36,.78)" />
            <stop offset="100%" stopColor="rgba(34,211,238,.7)" />
          </linearGradient>
        </defs>
        {balls.filter((ball) => ball.role === 'zap' && ball.trail && ball.trail.length > 1).map((ball) => {
          const trail = ball.trail ?? [];
          const lastPoint = trail.at(-1);
          const previousPoint = trail.at(-2) ?? lastPoint;
          const tailAngle = lastPoint && previousPoint ? Math.atan2(lastPoint.y - previousPoint.y, lastPoint.x - previousPoint.x) : 0;
          const tailOpacity = Math.min(0.86, Math.max(0, (ball.expiresAt - renderTime) / 1200));

          return (
            <g key={`${ball.id}-trail`} opacity={tailOpacity}>
              {trail.slice(1).map((point, index) => {
                const previous = trail[index];
                const progress = (index + 1) / (trail.length - 1);
                const outerOpacity = 0.03 + progress * 0.22;
                const innerOpacity = 0.08 + progress * 0.72;

                return (
                  <g key={`${ball.id}-trail-${index}`}>
                    <line
                      x1={previous.x}
                      y1={previous.y}
                      x2={point.x}
                      y2={point.y}
                      stroke={`hsla(${ball.hue}, 96%, 63%, ${outerOpacity})`}
                      strokeLinecap="round"
                      strokeWidth={ball.radius * (0.28 + progress * 1.08)}
                    />
                    <line
                      x1={previous.x}
                      y1={previous.y}
                      x2={point.x}
                      y2={point.y}
                      stroke={`hsla(${ball.hue}, 100%, 72%, ${innerOpacity})`}
                      strokeLinecap="round"
                      strokeWidth={Math.max(2.5, ball.radius * (0.08 + progress * 0.4))}
                    />
                  </g>
                );
              })}
              {lastPoint && (
                <g transform={`translate(${lastPoint.x} ${lastPoint.y}) rotate(${tailAngle * 180 / Math.PI})`}>
                  <path
                    d={`M ${-ball.radius * 2.8} 0 C ${-ball.radius * 1.6} ${-ball.radius * 0.9}, ${-ball.radius * 0.72} ${-ball.radius * 0.42}, 0 0 C ${-ball.radius * 0.72} ${ball.radius * 0.42}, ${-ball.radius * 1.6} ${ball.radius * 0.9}, ${-ball.radius * 2.8} 0 Z`}
                    fill={`hsla(${ball.hue}, 100%, 66%, .38)`}
                  />
                </g>
              )}
            </g>
          );
        })}
        {threadLinks.map(({ id, from, to, relation }) => {
          const fromOpacity = Math.min(1, Math.max(0, (from.expiresAt - renderTime) / 2200));
          const toOpacity = Math.min(1, Math.max(0, (to.expiresAt - renderTime) / 2200));
          const opacity = Math.min(fromOpacity, toOpacity, relation === 'root' ? 0.82 : 0.68);
          const midX = (from.x + to.x) / 2;
          const midY = (from.y + to.y) / 2 - Math.min(88, Math.hypot(to.x - from.x, to.y - from.y) * 0.12);

          return (
            <g key={id} opacity={opacity}>
              <path
                d={`M ${from.x} ${from.y} Q ${midX} ${midY} ${to.x} ${to.y}`}
                fill="none"
                stroke={relation === 'root' ? 'url(#thread-string-gradient)' : 'rgba(168,85,247,.72)'}
                strokeLinecap="round"
                strokeWidth={relation === 'root' ? '2.5' : '2'}
                strokeDasharray={relation === 'root' ? '7 9' : '3 8'}
                className="motion-safe:animate-pulse"
              />
              <circle cx={from.x} cy={from.y} r={relation === 'root' ? '4' : '3.5'} fill={relation === 'root' ? 'rgba(251,191,36,.85)' : 'rgba(168,85,247,.8)'} />
              <circle cx={to.x} cy={to.y} r="3" fill="rgba(34,211,238,.8)" />
            </g>
          );
        })}
      </svg>

      {balls.map((ball) => {
        const age = renderTime - ball.createdAt;
        const lifetime = ball.expiresAt - ball.createdAt;
        const fadeOut = Math.min(1, Math.max(0, (ball.expiresAt - renderTime) / 2200));
        const fadeIn = Math.min(1, age / 450);
        const opacity = Math.min(fadeIn, fadeOut);
        const isRoot = ball.role === 'root';
        const isReply = ball.role === 'reply';
        const isZap = ball.role === 'zap';
        const isReaction = ball.role === 'reaction';
        const label = isReaction ? 'reaction' : isZap ? 'zap' : isReply ? 'reply' : 'note';
        const cracks = ball.cracks ?? 0;
        const isExploding = ball.explodingAt !== undefined;
        const explosionProgress = isExploding
          ? Math.min(1, Math.max(0, (renderTime - (ball.explodingAt ?? renderTime)) / EXPLOSION_DURATION))
          : 0;
        const explosionFade = 1 - explosionProgress;
        const bubbleOpacity = isExploding ? Math.max(0, explosionFade * 0.85) : 1;
        const bubbleScale = isExploding ? 1 + explosionProgress * 0.18 : 1;
        const maskUrl = ball.shape ? getEmojiMaskUrl(ball.shape) : '';
        const hasShape = !!maskUrl;
        const shapeMaskStyle: CSSProperties = hasShape
          ? {
              WebkitMaskImage: `url(${maskUrl})`,
              maskImage: `url(${maskUrl})`,
              WebkitMaskSize: '100% 100%',
              maskSize: '100% 100%',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
            }
          : {};

        return (
          <div
            key={ball.id}
            className={`pointer-events-none absolute will-change-transform ${isZap ? 'z-30 rounded-full' : isReaction ? 'z-[1]' : 'z-10 rounded-full'}`}
            style={{
              width: isReaction ? ball.radius * 5.4 : ball.radius * 2,
              height: isReaction ? ball.radius * 2.45 : ball.radius * 2,
              transform: `translate3d(${ball.x - ball.radius}px, ${ball.y - ball.radius}px, 0)`,
              opacity: isReaction ? opacity * 0.82 : opacity,
              filter: `drop-shadow(0 0 ${Math.round(ball.radius * (isRoot ? 0.78 : isZap ? 1.25 : isReaction ? 1.9 : 0.55))}px hsla(${ball.hue}, 95%, 62%, ${isZap || isReaction ? '.9' : '.55'}))`,
            }}
          >
            {isReaction ? (
              <div className="relative flex h-full w-full items-center gap-2 rounded-full border border-fuchsia-100/20 bg-black/22 px-2 py-1 shadow-2xl shadow-fuchsia-950/20 backdrop-blur-[2px]">
                <div
                  className="absolute inset-y-1/2 right-[72%] h-1.5 -translate-y-1/2 rounded-full bg-gradient-to-l from-fuchsia-200/55 via-cyan-200/20 to-transparent blur-sm"
                  style={{ width: ball.radius * 4.2 }}
                />
                <div
                  className={`relative h-full aspect-square shrink-0 overflow-hidden ${hasShape ? '' : 'rounded-full border border-white/55 bg-black/40 shadow-[0_0_18px_rgba(255,255,255,.22)]'}`}
                  style={shapeMaskStyle}
                >
                  <img
                    src={ball.imageUrl}
                    alt="Nostr reaction avatar"
                    draggable={false}
                    className="h-full w-full object-cover opacity-90 saturate-125"
                    onError={(event) => {
                      event.currentTarget.src = DEFAULT_PROFILE_IMAGE;
                    }}
                  />
                  {!hasShape && (
                    <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_32%_22%,rgba(255,255,255,.62),transparent_24%),linear-gradient(135deg,rgba(255,255,255,.18),transparent_48%,rgba(0,0,0,.42))]" />
                  )}
                </div>
                <div className="relative grid min-w-8 place-items-center rounded-full border border-white/15 bg-white/14 px-2 py-1 text-xl font-black leading-none shadow-lg shadow-black/25">
                  <span className="drop-shadow-[0_2px_8px_rgba(0,0,0,.75)]">{ball.messagePreview.slice(0, 4)}</span>
                </div>
              </div>
            ) : (
              <>
            <div
              className="absolute inset-0"
              style={{
                opacity: bubbleOpacity,
                transform: `scale(${bubbleScale})`,
                transformOrigin: 'center',
              }}
            >
              {isRoot && (
                <div className="absolute -inset-4 rounded-full border border-amber-200/45 bg-amber-300/10 shadow-[0_0_32px_rgba(251,191,36,.22)]" />
              )}
              <div
                className="absolute -inset-2 rounded-full opacity-60 blur-md"
                style={{ background: `hsla(${ball.hue}, 95%, 58%, .38)` }}
              />
              <img
                src={ball.imageUrl}
                alt={`Nostr ${label} orb`}
                draggable={false}
                className={`absolute inset-0 h-full w-full object-cover shadow-[inset_0_0_18px_rgba(255,255,255,.5)] ${hasShape ? '' : `rounded-full border-2 ${isRoot ? 'border-amber-100' : isZap ? 'border-yellow-100 ring-2 ring-yellow-300/70' : isReply ? 'border-cyan-100/90' : 'border-white/75'}`}`}
                style={shapeMaskStyle}
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_PROFILE_IMAGE;
                }}
              />
              <div
                className={`absolute inset-0 bg-[radial-gradient(circle_at_32%_24%,rgba(255,255,255,.72),transparent_18%),linear-gradient(135deg,rgba(255,255,255,.24),transparent_45%,rgba(0,0,0,.38))] ${hasShape ? '' : 'rounded-full'}`}
                style={shapeMaskStyle}
              />
              <div
                className="absolute left-1/2 top-1/2 h-1 w-1 rounded-full bg-white/80"
                style={{
                  boxShadow: `0 0 ${Math.max(14, ball.radius * 0.7)}px ${Math.max(7, ball.radius * 0.26)}px hsla(${ball.hue}, 95%, 72%, .34)`,
                  opacity: Math.max(0.08, 1 - age / lifetime),
                }}
              />
              {cracks > 0 && (
                <svg
                  className="pointer-events-none absolute inset-0 h-full w-full"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <g
                    stroke="rgba(255,255,255,.96)"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    style={{ filter: 'drop-shadow(0 0 1.2px rgba(0,0,0,.85))' }}
                  >
                    <path d="M 8 52 L 30 44 L 42 56 L 56 38 L 70 52 L 92 46" strokeWidth="2.6" />
                    <path d="M 30 44 L 24 30" strokeWidth="1.4" />
                    <path d="M 56 38 L 60 22" strokeWidth="1.4" />
                    {(cracks >= 2 || isExploding) && (
                      <>
                        <path d="M 48 8 L 56 30 L 42 44 L 58 60 L 44 78 L 52 92" strokeWidth="2.6" />
                        <path d="M 56 30 L 70 24" strokeWidth="1.4" />
                        <path d="M 58 60 L 76 64" strokeWidth="1.4" />
                      </>
                    )}
                  </g>
                </svg>
              )}
            </div>
            {isExploding && (
              <>
                <div
                  className="pointer-events-none absolute left-1/2 top-1/2 rounded-full"
                  style={{
                    width: ball.radius * 2 * (1 + explosionProgress * 1.7),
                    height: ball.radius * 2 * (1 + explosionProgress * 1.7),
                    transform: 'translate(-50%, -50%)',
                    border: `${Math.max(1.5, ball.radius * 0.22 * explosionFade)}px solid hsla(${ball.hue}, 100%, 72%, ${explosionFade})`,
                    boxShadow: `0 0 ${ball.radius * (1.4 + explosionProgress * 2.4)}px hsla(${ball.hue}, 100%, 70%, ${0.55 * explosionFade})`,
                  }}
                />
                <div
                  className="pointer-events-none absolute left-1/2 top-1/2 rounded-full"
                  style={{
                    width: ball.radius * 2 * (1 + explosionProgress * 2.8),
                    height: ball.radius * 2 * (1 + explosionProgress * 2.8),
                    transform: 'translate(-50%, -50%)',
                    border: `${Math.max(1, ball.radius * 0.12 * explosionFade)}px solid hsla(50, 100%, 78%, ${0.85 * explosionFade})`,
                  }}
                />
                {EXPLOSION_PARTICLE_ANGLES.map((angle, i) => {
                  const distance = ball.radius * (1.05 + explosionProgress * 2.3);
                  const px = Math.cos(angle) * distance;
                  const py = Math.sin(angle) * distance;
                  const size = Math.max(2, ball.radius * 0.22 * (1 - explosionProgress * 0.5));
                  return (
                    <div
                      key={i}
                      className="pointer-events-none absolute left-1/2 top-1/2 rounded-full"
                      style={{
                        width: size,
                        height: size,
                        transform: `translate(${px - size / 2}px, ${py - size / 2}px)`,
                        background: `hsla(${(ball.hue + i * 14) % 360}, 100%, 70%, ${explosionFade})`,
                        boxShadow: `0 0 ${size * 2.2}px hsla(${ball.hue}, 100%, 72%, ${0.85 * explosionFade})`,
                      }}
                    />
                  );
                })}
              </>
            )}
            {!isExploding && (
              <div
                className={`absolute left-1/2 top-full mt-2 max-w-56 -translate-x-1/2 rounded-xl border px-3 py-2 text-center font-medium leading-snug shadow-xl shadow-black/30 backdrop-blur-md ${isRoot ? 'border-amber-200/25 bg-amber-950/55 text-amber-50' : isZap ? 'border-yellow-200/40 bg-yellow-950/70 text-yellow-50' : 'border-white/15 bg-black/45 text-white/82'} ${isZap ? 'text-[12px]' : 'text-[11px]'}`}
                style={{
                  width: isZap ? Math.min(112, Math.max(72, ball.radius * 4.1)) : Math.min(224, Math.max(116, ball.radius * 3.1)),
                }}
              >
                <div className={`absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 border-l border-t ${isRoot ? 'border-amber-200/25 bg-amber-950/55' : isZap ? 'border-yellow-200/40 bg-yellow-950/70' : 'border-white/15 bg-black/45'}`} />
                {!isRoot && !isZap && (
                  <div className={`mb-1 text-[9px] font-black uppercase tracking-[0.2em] ${isReply ? 'text-cyan-200' : 'text-white/45'}`}>
                    {label}
                  </div>
                )}
                {isZap ? (
                  <>
                    <div className="text-[9px] font-black uppercase tracking-[0.22em] text-yellow-200/85">zap</div>
                    <div className="mt-0.5 text-sm font-black tabular-nums">{formatSats(ball.zapAmountSats ?? 0)}</div>
                    <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-yellow-100/68">sats</div>
                  </>
                ) : (
                  <p className="relative line-clamp-2 break-words">
                    {ball.messagePreview}
                  </p>
                )}
              </div>
            )}
              </>
            )}
          </div>
        );
      })}

      {balls.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-6 text-center">
          <div className="max-w-xl rounded-3xl border border-dashed border-white/18 bg-black/20 p-8 backdrop-blur-sm">
            <Sparkles className="mx-auto mb-4 h-10 w-10 text-cyan-200" />
            <p className="text-2xl font-bold text-white">Waiting for notes and zaps…</p>
            <p className="mt-2 text-white/62">
              Live posts become bouncing orbs. Paid zap receipts become fast profile-image comets with fading sat trails.
            </p>
          </div>
        </div>
      )}

      <footer className="absolute bottom-4 right-4 z-20 text-sm text-white/55">
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noreferrer"
          className="block w-fit rounded-full border border-white/10 bg-black/30 px-3 py-2 text-white/70 underline-offset-4 backdrop-blur-md transition hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
        >
          Vibed with Shakespeare
        </a>
      </footer>
    </main>
  );
};

export default Index;
